
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Check, Coins } from 'lucide-react';
import { Task } from '@/types/task';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { fetchCatImage } from '@/utils/catApi';

interface TaskActionsProps {
  task: Task;
  onComplete: (task: Task) => void;
}

export const TaskActions = ({ task, onComplete }: TaskActionsProps) => {
  const [showDialog, setShowDialog] = useState(false);
  const [catImage, setCatImage] = useState<string | null>(null);
  const [isCompleting, setIsCompleting] = useState(false);
  const { toast } = useToast();


  const updateWeeklyBalance = async (coinsToAdd: number) => {
    try {
      // Get the latest balance record
      const { data: latestBalance, error: fetchError } = await supabase
        .from('weekly_balances')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (fetchError) throw fetchError;

      const currentAmount = latestBalance?.amount || 0;
      const newAmount = currentAmount + coinsToAdd;

      // Insert a new balance record with the updated amount
      const { error: insertError } = await supabase
        .from('weekly_balances')
        .insert([{ amount: newAmount }]);

      if (insertError) throw insertError;
    } catch (error) {
      console.error('Error updating weekly balance:', error);
    }
  };

  const handleCompleteClick = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent the event from bubbling up to the card
    
    if (isCompleting) return;

    setIsCompleting(true);
    const imageUrl = await fetchCatImage();
    if (imageUrl) {
      setCatImage(imageUrl);
      setShowDialog(true);
      
      // If the task has coins, update the weekly balance
      if (task.coins && task.coins > 0) {
        await updateWeeklyBalance(task.coins);
      }
    } else {
      toast({
        title: "Error",
        description: "Failed to fetch cat image",
        variant: "destructive",
      });
      setIsCompleting(false);
    }
  };

  const handleDialogClose = (open: boolean) => {
    if (!open) {
      setShowDialog(false);
      setIsCompleting(false);
      // Only complete the task when the dialog is actually closed
      onComplete(task);
    }
  };

  if (task.isCompleted) {
    return null;
  }

  return (
    <>
      <div className="flex justify-start space-x-2 items-center">
        <Button
          onClick={handleCompleteClick}
          size="sm"
          className="bg-cat-primary hover:bg-cat-primary/80"
          disabled={isCompleting}
        >
          <Check className="mr-1 h-4 w-4" />
          Complete
        </Button>
        
        {task.coins > 0 && (
          <div className="flex items-center gap-1 text-cat-primary ml-2">
            <Coins className="h-4 w-4" />
            <span className="text-sm font-medium">{task.coins}</span>
          </div>
        )}
      </div>

      <Dialog open={showDialog} onOpenChange={handleDialogClose}>
        <DialogContent className="sm:max-w-md">
          <DialogTitle className="text-xl font-semibold text-center">Task Completed! 🎉</DialogTitle>
          <div className="flex flex-col items-center space-y-4">
            <p className="text-center text-gray-600">Here's a cat to celebrate your achievement!</p>
            {catImage && (
              <img 
                src={catImage} 
                alt="Celebration Cat" 
                className="rounded-lg max-h-[300px] w-auto object-contain animate-fade-in"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
